import React, { useState } from "react";
import { Card, Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { FaDatabase, FaPlus, FaQuestionCircle } from "react-icons/fa";
import { Icon } from "../../../../../components/common/Icon";
import SourcesListTable from "./SourcesListTable";
import AddSourceModal from "./AddSourceModal";
import EditSourceModal from "./EditSourceModal";
import IngestionHistoryModal from "./IngestionHistoryModal";
import VideoHelpModal from "../../../../../components/common/VideoHelpModal";
import ConfirmationModal from "../../../../../components/common/ConfirmationModal";
import {
  listSourceJobs,
  updateKnowledgeSource,
  clearChatbotCache,
} from "../../../../../features/ai_chatbots/aiChatbotsService";

interface KnowledgeSourcesProps {
  chatbotId?: number;
  sources: any[];
  setSources: React.Dispatch<React.SetStateAction<any[]>>;
  sourceStatsMap: Record<
    number,
    { chunks: number; chars: number; tokensEst: number }
  >;
  selectedSourceId: number | null;
  setSelectedSourceId: (id: number | null) => void;
  newSourceTitle: string;
  setNewSourceTitle: (title: string) => void;
  newSourceLang: string;
  setNewSourceLang: (lang: string) => void;
  newText: string;
  setNewText: (text: string) => void;
  contentType: "text" | "file";
  setContentType: (type: "text" | "file") => void;
  fileUploading: boolean;
  jobs: any[];
  polling: boolean;
  handleCreateSource: (params?: {
    title: string;
    lang: string;
    type: string;
  }) => Promise<any>;
  handleIngestText: (sourceId?: number, text?: string) => Promise<void>;
  handleFileUpload: (file: File, sourceId: number) => Promise<void>;
  handleDeleteSource: (sourceId: number) => Promise<void>;
  refreshSources: () => Promise<void>;
  isEditMode: boolean;
}

const KnowledgeSources: React.FC<KnowledgeSourcesProps> = ({
  chatbotId,
  sources,
  setSources,
  sourceStatsMap,
  selectedSourceId,
  setSelectedSourceId,
  newSourceTitle,
  setNewSourceTitle,
  newSourceLang,
  setNewSourceLang,
  newText,
  setNewText,
  contentType,
  setContentType,
  fileUploading,
  jobs: parentJobs, // We might use local jobs for history modal
  polling,
  handleCreateSource,
  handleIngestText,
  handleFileUpload,
  handleDeleteSource,
  refreshSources,
  isEditMode,
}) => {
  const { t } = useTranslation();

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);

  React.useEffect(() => {
    const hasSeenTutorial = localStorage.getItem("seen_sources_tutorial");
    if (!hasSeenTutorial) {
      setShowVideoModal(true);
      localStorage.setItem("seen_sources_tutorial", "true");
    }
  }, []);

  const [editSource, setEditSource] = useState<any | null>(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historySource, setHistorySource] = useState<any | null>(null);
  const [historyJobs, setHistoryJobs] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Delete confirmation
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState<any | null>(null);

  // Submitting state for add modal
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle Add Source Submit
  const handleAddSourceSubmit = async (data: {
    type: "text" | "file";
    title: string;
    lang: string;
    text?: string;
    file?: File;
  }) => {
    setIsSubmitting(true);
    try {
      const created = await handleCreateSource({
        title: data.title,
        lang: data.lang,
        type: data.type,
      });

      if (!created) throw new Error("Failed to create source");

      if (data.type === "text" && data.text) {
        await handleIngestText(created.id, data.text);
      } else if (data.type === "file" && data.file) {
        await handleFileUpload(data.file, created.id);
      }

      // Refresh sources list after successful add
      await refreshSources();

      // Clear chatbot cache if chatbotId is present
      if (chatbotId) {
        try {
          await clearChatbotCache(chatbotId);
        } catch (cacheError) {
          console.error("Failed to clear chatbot cache:", cacheError);
        }
      }
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const fetchHistory = async (source: any) => {
    if (!chatbotId) return;
    setHistorySource(source);
    setHistoryLoading(true);
    setShowHistoryModal(true);
    try {
      const list = await listSourceJobs(chatbotId, source.id, { limit: 20 });
      setHistoryJobs(list);
    } catch (e) {
      console.error(e);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Handle Edit Source Submit
  const handleEditSourceSubmit = async (data: {
    title: string;
    lang: string;
    text?: string;
    file?: File;
  }) => {
    if (!editSource || !chatbotId) return;
    setIsSubmitting(true);
    try {
      // 1. Update source metadata (title, lang) if changed
      if (data.title !== editSource.title || data.lang !== editSource.lang) {
        await updateKnowledgeSource(chatbotId, editSource.id, {
          title: data.title,
          lang: data.lang,
        });

        // Update local state
        setSources((prev) =>
          prev.map((s) =>
            s.id === editSource.id
              ? { ...s, title: data.title, lang: data.lang }
              : s
          )
        );
      }

      // 2. Ingest new content (creates new version)
      const isFile = editSource.type?.toLowerCase() === "file";

      if (!isFile && data.text) {
        await handleIngestText(editSource.id, data.text);
      } else if (isFile && data.file) {
        await handleFileUpload(data.file, editSource.id);
      }

      // Refresh sources list after successful edit
      await refreshSources();

      // Clear chatbot cache if chatbotId is present
      if (chatbotId) {
        try {
          await clearChatbotCache(chatbotId);
        } catch (cacheError) {
          console.error("Failed to clear chatbot cache:", cacheError);
        }
      }
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Card className="glass-card mb-3">
        <div className="premium-helper-banner d-flex justify-content-between align-items-center mb-4 p-3 rounded-3 border-start border-4 border-info shadow-sm bg-light bg-opacity-50">
          <div className="d-flex align-items-center">
            <div className="icon-badge me-3 bg-info bg-opacity-10 p-2 rounded-circle d-flex align-items-center justify-content-center">
              <Icon icon={FaQuestionCircle} className="text-info" size="1.25rem" />
            </div>
            <div>
              <h6 className="mb-0 fw-bold text-dark">{t("chatbots.training_guide", "Training Guide")}</h6>
            </div>
          </div>
          <Button
            variant="info"
            size="sm"
            className="rounded-pill px-4 fw-600 text-white shadow-sm hover-elevate"
            onClick={() => setShowVideoModal(true)}
          >
            <Icon icon={FaQuestionCircle} className="me-2" />
            {t("chatbots.watch_tutorial", "Watch Tutorial")}
          </Button>
        </div>
        <Card.Header className="gradient-card-header d-flex justify-content-between align-items-center">
          <div className="sources-header-content">
            <div className="sources-header-info">
              <h6 className="sources-title mb-0">
                <Icon icon={FaDatabase} className="me-2" />
                {t("chatbots_edit.knowledge_sources")}
              </h6>
              <small className="sources-subtitle">
                {sources.length}{" "}
                {sources.length !== 1
                  ? t("chatbots_edit.sources")
                  : t("chatbots_edit.source")}{" "}
                • {sources.filter((s) => s.is_active).length}{" "}
                {t("chatbots_edit.active_lowercase")}
              </small>
            </div>
          </div>
          <div className="sources-header-actions d-flex gap-2">
            <Button
              variant="secondary"
              id="train_data_btn"
              name="train_data_btn"
              size="sm"
              className="d-flex align-items-center"
              onClick={() => setShowAddModal(true)}
              disabled={!isEditMode}
              style={{
                backgroundColor: "var(--brand-primary)",
                color: "white",
              }}
            >
              <Icon icon={FaPlus} className="me-2" />
              {t("chatbots.train_data", "Train Data")}
            </Button>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          <SourcesListTable
            sources={sources}
            sourceStatsMap={sourceStatsMap}
            isEditMode={isEditMode}
            onDelete={(s) => {
              setDeleteCandidate(s);
              setShowDeleteModal(true);
            }}
            onShowHistory={(s) => fetchHistory(s)}
            onEdit={(s) => {
              setEditSource(s);
              setShowEditModal(true);
            }}
          />
        </Card.Body>
      </Card>

      <AddSourceModal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        onSubmit={handleAddSourceSubmit}
        isSubmitting={isSubmitting}
        chatbotId={chatbotId}
      />

      <EditSourceModal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setEditSource(null);
        }}
        chatbotId={chatbotId}
        source={editSource}
        onSubmit={handleEditSourceSubmit}
        isSubmitting={isSubmitting}
      />

      <IngestionHistoryModal
        show={showHistoryModal}
        onHide={() => setShowHistoryModal(false)}
        source={historySource}
        jobs={historyJobs}
        loading={historyLoading}
        onRefresh={() => historySource && fetchHistory(historySource)}
      />

      <ConfirmationModal
        show={showDeleteModal}
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={async () => {
          if (deleteCandidate) {
            try {
              await handleDeleteSource(deleteCandidate.id);
              setShowDeleteModal(false);
              setDeleteCandidate(null);

              // Clear chatbot cache if chatbotId is present
              if (chatbotId) {
                try {
                  await clearChatbotCache(chatbotId);
                } catch (cacheError) {
                  console.error("Failed to clear chatbot cache:", cacheError);
                }
              }
            } catch (error) {
              console.error("Delete failed:", error);
            }
          }
        }}
        title={t("chatbots_edit.confirm_delete")}
        message={t("chatbots_edit.delete_source_confirm", {
          title: deleteCandidate?.title || "(untitled)",
        })}
        confirmText={t("delete")}
        cancelText={t("cancel")}
      />

      <VideoHelpModal
        show={showVideoModal}
        onHide={() => setShowVideoModal(false)}
        videoUrl="/training/train_data_sources.mp4"
        title={t(
          "chatbots.tutorial_sources",
          "Tutorial: Training with Sources"
        )}
      />
    </>
  );
};

export default KnowledgeSources;
